import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import api from '../services/api';

interface PublicPlan {
  _id: string;
  name: string;
  description?: string;
  monthlyFee: number;
  annualFee: number;
  billingPeriod: string;
  features: string[];
  trialDays?: number;
  sortOrder: number;
  unitLabel?: string;
  metadata?: { planType?: string };
}

const Plans = () => {
  const [searchParams] = useSearchParams();
  const selectedPlanFromUrl = (searchParams.get('plan') || '').toLowerCase();
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<PublicPlan[]>('/public/plans');
        if (!cancelled) {
          setPlans(res.data || []);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load plans');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
        <Box sx={{ mt: 2 }}>
          <Button component={RouterLink} to="/register" variant="contained">
            Continue to registration
          </Button>
        </Box>
      </Container>
    );
  }

  const basePlans = plans
    .filter((p) => p.metadata?.planType !== 'addon' && p.metadata?.planType !== 'contractor')
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  const isIndividualPlan = (p: PublicPlan) =>
    ['individual', 'elite individual'].includes(p.name?.toLowerCase() ?? '');
  const agencyPlans = basePlans.filter((p) => !isIndividualPlan(p));
  const individualPlans = basePlans.filter(isIndividualPlan);

  const renderPlanCard = (plan: PublicPlan) => {
    const isFree = ['free', 'agency free'].includes(plan.name?.toLowerCase() ?? '');
    const isIndividual = isIndividualPlan(plan);
    const planSlug = plan.name?.toLowerCase() || '';
    const isSelected = selectedPlanFromUrl === planSlug;
    const priceDisplay =
      plan.monthlyFee === 0
        ? 'Free'
        : `$${plan.monthlyFee}/${plan.unitLabel || 'user'}/month`;
    const annualDisplay =
      plan.monthlyFee === 0 || plan.billingPeriod === 'monthly'
        ? null
        : `$${plan.annualFee}/user/year`;

    return (
      <Grid item xs={12} md={4} key={plan._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '2px solid',
                  borderColor: isSelected
                    ? isFree
                      ? 'success.main'
                      : isIndividual
                        ? '#7c3aed'
                        : 'primary.main'
                    : isFree
                      ? 'success.light'
                      : isIndividual
                        ? '#ede9fe'
                        : 'primary.light',
                  boxShadow: isSelected ? 6 : 2,
                }}
              >
                <CardHeader
                  title={plan.name}
                  sx={{
                    background: isFree
                      ? 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)'
                      : isIndividual
                        ? 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)'
                        : 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                    color: 'white',
                    textAlign: 'center',
                    '& .MuiCardHeader-title': { fontSize: '1.5rem', fontWeight: 700 },
                  }}
                />
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="h4" fontWeight={700} color="primary.main">
                      {priceDisplay}
                    </Typography>
                    {annualDisplay && (
                      <Typography variant="body2" color="text.secondary">
                        or {annualDisplay}
                      </Typography>
                    )}
                    {plan.trialDays > 0 && (
                      <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                        {plan.trialDays}-day free trial
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    {(plan.features || []).map((feature, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        {/^❌/.test(feature.trim()) ? (
                          <>
                            <CancelIcon sx={{ color: 'error.main', fontSize: 20, mt: 0.2 }} />
                            <Typography variant="body2" color="text.secondary">
                              {feature.replace(/^❌\s*/, '')}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20, mt: 0.2 }} />
                            <Typography variant="body2">
                              {feature.replace(/^✅\s*/, '')}
                            </Typography>
                          </>
                        )}
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Button
                      component={RouterLink}
                      to="/register"
                      variant={isFree ? 'outlined' : 'contained'}
                      color={isFree ? 'success' : 'primary'}
                      fullWidth
                      size="large"
                    >
                      {isFree ? 'Get started free' : 'Register & subscribe'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
      </Grid>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
      <Box sx={{ mb: 4 }}>
        <Button
          component={RouterLink}
          to="/"
          variant="outlined"
          size="small"
          sx={{ textTransform: 'none' }}
        >
          ← Back to main page
        </Button>
      </Box>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" fontWeight={700} gutterBottom>
          Plans &amp; Pricing
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
          Hurry up — register now and enjoy 2 months of Agency Premium plan free. No credit card, no commitment. Add up to 5 users (including admin) during your trial.
        </Typography>
        <Box sx={{ mt: 3 }}>
          <Button
            component={RouterLink}
            to="/register"
            variant="contained"
            size="large"
            sx={{
              px: 3,
              boxShadow: '0 18px 40px rgba(33, 150, 243, 0.25)',
              '&:hover': { boxShadow: '0 20px 50px rgba(33, 150, 243, 0.32)' },
            }}
          >
            Register your agency
          </Button>
        </Box>
      </Box>

      {/* Agency plans section */}
      {agencyPlans.length > 0 && (
        <Box sx={{ mb: 8 }}>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
            For agencies
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Team plans for real estate agencies. Invite members, manage commission, and use third-party integrations.
          </Typography>
          <Grid container spacing={3} justifyContent="flex-start">
            {agencyPlans.map(renderPlanCard)}
          </Grid>
        </Box>
      )}

      {/* Individual plans section */}
      {individualPlans.length > 0 && (
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
            For solo agents
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            One-seat plan for individual agents. Full features except team, commission, and integrations. Cannot switch to Agency plan.
          </Typography>
          <Grid container spacing={3} justifyContent="flex-start">
            {individualPlans.map(renderPlanCard)}
          </Grid>
        </Box>
      )}

      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <Typography variant="body2" color="text.secondary">
          All plans include mobile app access. Need help choosing?{' '}
          <Link component={RouterLink} to="/contact-sales">
            Contact sales
          </Link>
        </Typography>
      </Box>

      {/* Footer (same as main page) */}
      <Box
        sx={{
          mt: 8,
          mb: 10,
          pt: 4,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          pr: { xs: 0, sm: '80px' },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} CodePulse Pty Ltd
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Contact:{' '}
            <Link href="mailto:support@workflow.estate" underline="hover">
              support@workflow.estate
            </Link>
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: { xs: 'flex-start', sm: 'flex-end' },
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Link component={RouterLink} to="/#features" color="inherit" underline="hover" sx={{ fontSize: '0.875rem' }}>
              Features
            </Link>
            <Link component={RouterLink} to="/support" color="inherit" underline="hover" sx={{ fontSize: '0.875rem' }}>
              Support
            </Link>
            <Link component={RouterLink} to="/contact-sales" color="inherit" underline="hover" sx={{ fontSize: '0.875rem' }}>
              Contact
            </Link>
            <Link component={RouterLink} to="/privacy" color="inherit" underline="hover" sx={{ fontSize: '0.875rem' }}>
              Privacy Policy
            </Link>
            <Link component={RouterLink} to="/login" color="inherit" underline="hover" sx={{ fontSize: '0.875rem' }}>
              Log In
            </Link>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default Plans;
